import { categorizeUser } from "./userCategorizer";

export type FitStatus = "ok" | "warn" | "bad";

export interface UserFit {
  gender: FitStatus;
  age: FitStatus;
  neighborhood: FitStatus;
  dietary: FitStatus;
}

export interface FitInputUser {
  quizAnswers?: any;
  specialRequests?: string;
  neighborhood?: string;
}

function parseSpecial(specialRequests?: string): any {
  if (!specialRequests) return {};
  try {
    return JSON.parse(specialRequests);
  } catch {
    return {};
  }
}

const DIETARY_TAG_BY_FLAG: Record<string, string> = {
  vegetarian: "has_vegetarian_options",
  vegan: "has_vegan_options",
  glutenFree: "has_gluten_free_options",
  halal: "has_halal_options",
  kosher: "has_kosher_options",
  pescatarian: "has_pescatarian_options",
};

function dietaryNeedsFor(special: any): string[] {
  const needs: string[] = [];
  for (const [flag, tag] of Object.entries(DIETARY_TAG_BY_FLAG)) {
    if (special[flag]) needs.push(tag);
  }
  return needs;
}

export function computeUserFit(
  user: FitInputUser,
  tablemates: FitInputUser[],
  restaurantNeighborhoodName?: string,
  restaurantTags?: string[],
): UserFit {
  const userCat = categorizeUser(user);
  const userAge = userCat.age;
  const special = parseSpecial(user.specialRequests);
  const genderPrefs: string[] = (special.gender || []).map((s: string) =>
    String(s).toLowerCase(),
  );
  const agePrefs: string[] = (special.age || []).map((s: string) =>
    String(s).toLowerCase(),
  );

  // GENDER
  let gender: FitStatus = "ok";
  if (tablemates.length > 0) {
    if (genderPrefs.includes("sapphic")) {
      const sapphicMates = tablemates.filter((m) => {
        const cat = categorizeUser(m);
        return cat.gender === "F" || cat.gender === "NB";
      });
      const ratio = sapphicMates.length / tablemates.length;
      gender = ratio >= 0.66 ? "ok" : ratio >= 0.34 ? "warn" : "bad";
    } else if (genderPrefs.includes("male")) {
      gender = tablemates.some((m) => categorizeUser(m).gender === "M")
        ? "ok"
        : "bad";
    } else if (genderPrefs.includes("same")) {
      gender = tablemates.some(
        (m) => categorizeUser(m).gender === userCat.gender,
      )
        ? "ok"
        : "bad";
    }
  }

  // AGE — union semantics: if any selected age pref is satisfied, the user
  // got at least part of what they asked for. all=ok, some=warn, none=bad.
  let age: FitStatus = "ok";
  if (tablemates.length > 0 && userAge !== null) {
    const mateAges = tablemates
      .map((m) => categorizeUser(m).age)
      .filter((a): a is number => a !== null);
    if (mateAges.length > 0) {
      const selectedPrefs = agePrefs.length === 0 ? ["my_age"] : agePrefs;
      const isSatisfied = (pref: string): boolean => {
        if (pref === "older") return mateAges.some((a) => a >= userAge + 5);
        if (pref === "younger") return mateAges.some((a) => a <= userAge - 5);
        if (pref === "my_age")
          return mateAges.some((a) => Math.abs(a - userAge) <= 10);
        return true;
      };
      const metCount = selectedPrefs.filter(isSatisfied).length;
      if (metCount === 0) age = "bad";
      else if (metCount < selectedPrefs.length) age = "warn";
    }
  }

  // NEIGHBORHOOD
  let neighborhood: FitStatus = "ok";
  if (user.neighborhood && restaurantNeighborhoodName) {
    neighborhood =
      user.neighborhood.toLowerCase() ===
      restaurantNeighborhoodName.toLowerCase()
        ? "ok"
        : "bad";
  }

  // DIETARY
  let dietary: FitStatus = "ok";
  const needs = dietaryNeedsFor(special);
  if (needs.length > 0) {
    const tags = new Set((restaurantTags || []).map((t) => t.toLowerCase()));
    const matched = needs.filter((tag) => tags.has(tag));
    if (matched.length === 0) dietary = "bad";
    else if (matched.length < needs.length) dietary = "warn";
  }

  return { gender, age, neighborhood, dietary };
}

/**
 * Per-preference age fit on a continuous 0-1 scale.
 *
 * - "older":  any mate in [user+1 .. user+20]y is a perfect match (1.0).
 *             Mates further past +20y are penalised at 0.05/year; floored at 0.
 *             No older mate at all → 0.
 * - "younger": symmetric to "older".
 * - "my_age":  any mate within ±10y → 1.0; falls off linearly to 0 at ±20y.
 *
 * The returned score is the best score across all tablemates (we reward the
 * single best match, not the average).
 */
function agePrefScore(
  pref: string,
  userAge: number,
  mateAges: number[],
): number {
  if (mateAges.length === 0) return 0;

  const scoreFor = (gap: number): number => {
    if (pref === "older") {
      if (gap <= 0) return 0;
      if (gap <= 20) return 1;
      return Math.max(0, 1 - (gap - 20) * 0.05);
    }
    if (pref === "younger") {
      if (gap >= 0) return 0;
      const mag = -gap;
      if (mag <= 20) return 1;
      return Math.max(0, 1 - (mag - 20) * 0.05);
    }
    // my_age
    const mag = Math.abs(gap);
    if (mag <= 10) return 1;
    if (mag >= 20) return 0;
    return 1 - (mag - 10) / 10;
  };

  let best = 0;
  for (const mateAge of mateAges) {
    const s = scoreFor(mateAge - userAge);
    if (s > best) best = s;
    if (best === 1) break;
  }
  return best;
}

export interface CompatBreakdown {
  score: number;
  gender: { score: number; label: string };
  age: { score: number; label: string };
}

/**
 * Compute the compat score and a human-readable breakdown of each dimension,
 * useful for explaining a non-perfect score in a tooltip.
 */
export function explainCompatScore(
  user: FitInputUser,
  tablemates: FitInputUser[],
): CompatBreakdown {
  const fit = computeUserFit(user, tablemates, undefined, undefined);
  const v = (s: FitStatus) => (s === "ok" ? 1 : s === "warn" ? 0.5 : 0);
  const genderScore = v(fit.gender);
  const special = parseSpecial(user.specialRequests);
  const genderPrefs: string[] = (special.gender || []).map((s: string) =>
    String(s).toLowerCase(),
  );

  // Gender label
  const wantedGender = ["sapphic", "male", "same"].find((p) =>
    genderPrefs.includes(p),
  );
  let genderLabel = "no preference";
  if (wantedGender) {
    if (tablemates.length === 0) {
      genderLabel = `wants ${wantedGender} — no tablemates yet`;
    } else {
      const cats = tablemates.map((m) => categorizeUser(m));
      if (wantedGender === "sapphic") {
        const sapphic = cats.filter(
          (c) => c.gender === "F" || c.gender === "NB",
        ).length;
        genderLabel = `wants sapphic — ${sapphic}/${cats.length} F or NB`;
      } else if (wantedGender === "male") {
        const males = cats.filter((c) => c.gender === "M").length;
        genderLabel = `wants male — ${males} M at table`;
      } else {
        const userCat = categorizeUser(user);
        const same = cats.filter((c) => c.gender === userCat.gender).length;
        genderLabel = `wants same (${userCat.gender}) — ${same} match`;
      }
    }
  }

  // Age
  const userCat = categorizeUser(user);
  let ageScore = 1;
  let ageLabel = "no preference";
  if (tablemates.length > 0 && userCat.age !== null) {
    const mateAges = tablemates
      .map((m) => categorizeUser(m).age)
      .filter((a): a is number => a !== null);
    if (mateAges.length > 0) {
      const agePrefs: string[] = (special.age || []).map((s: string) =>
        String(s).toLowerCase(),
      );
      const selectedPrefs = agePrefs.length === 0 ? ["my_age"] : agePrefs;
      const perPref = selectedPrefs.map((p) => ({
        pref: p,
        score: agePrefScore(p, userCat.age!, mateAges),
      }));
      ageScore =
        perPref.reduce((a, b) => a + b.score, 0) / perPref.length;
      ageLabel = perPref
        .map((x) => `${x.pref} ${Math.round(x.score * 100)}%`)
        .join(", ");
    }
  }

  return {
    score: (genderScore + ageScore) / 2,
    gender: { score: genderScore, label: genderLabel },
    age: { score: ageScore, label: ageLabel },
  };
}

/**
 * Single-number "compat score" 0-1 for a user given their tablemates.
 */
export function computeCompatScore(
  user: FitInputUser,
  tablemates: FitInputUser[],
): number {
  return explainCompatScore(user, tablemates).score;
}

export interface TableFitAxis {
  key: "gender" | "age" | "neighborhood" | "dietary" | "compatibility";
  label: string;
  requested: number; // 0-1 share of users who care about this dimension
  actual: number; // 0-1 share of users whose pref is satisfied (or 1.0 if they didn't care)
}

/**
 * Aggregate per-user fit signals into table-level "requested" vs "actual"
 * shares, suitable for plotting as two radar polygons.
 *
 * - requested: 1.0 means every user had a stated preference on this axis.
 * - actual: 1.0 means every user is satisfied on this axis.
 * - The gap (requested - actual) is the discrepancy you want to spot.
 */
export function computeTableFit(
  users: FitInputUser[],
  restaurantNeighborhoodName?: string,
  restaurantTags?: string[],
): TableFitAxis[] {
  if (users.length === 0) {
    return [
      { key: "gender", label: "Gender", requested: 0, actual: 1 },
      { key: "age", label: "Age", requested: 0, actual: 1 },
      { key: "compatibility", label: "Compat", requested: 0, actual: 1 },
    ];
  }

  let genderRequested = 0;
  let genderSatisfied = 0;
  let ageRequested = 0;
  let ageSatisfied = 0;

  for (let i = 0; i < users.length; i++) {
    const user = users[i];
    const others = users.filter((_, idx) => idx !== i);
    const special = parseSpecial(user.specialRequests);
    const genderPrefs: string[] = (special.gender || []).map((s: string) =>
      String(s).toLowerCase(),
    );
    const agePrefs: string[] = (special.age || []).map((s: string) =>
      String(s).toLowerCase(),
    );
    const fit = computeUserFit(
      user,
      others,
      restaurantNeighborhoodName,
      restaurantTags,
    );

    const hasGenderPref =
      genderPrefs.length > 0 &&
      (genderPrefs.includes("sapphic") ||
        genderPrefs.includes("male") ||
        genderPrefs.includes("same"));
    if (hasGenderPref) {
      genderRequested += 1;
      if (fit.gender === "ok") genderSatisfied += 1;
      else if (fit.gender === "warn") genderSatisfied += 0.5;
    } else {
      genderSatisfied += 1; // didn't care, considered satisfied
    }

    const hasAgePref =
      agePrefs.length > 0 &&
      (agePrefs.includes("older") ||
        agePrefs.includes("younger") ||
        agePrefs.includes("my_age"));
    if (hasAgePref) {
      ageRequested += 1;
      if (fit.age === "ok") ageSatisfied += 1;
      else if (fit.age === "warn") ageSatisfied += 0.5;
    } else {
      ageSatisfied += 1;
    }

  }

  const n = users.length;
  return [
    {
      key: "gender",
      label: "Gender",
      requested: genderRequested / n,
      actual: genderSatisfied / n,
    },
    {
      key: "age",
      label: "Age",
      requested: ageRequested / n,
      actual: ageSatisfied / n,
    },
    // Every user has a "compatibility group"; satisfied if at least one other
    // user shares it.
    (() => {
      let satisfied = 0;
      for (let i = 0; i < users.length; i++) {
        const u = users[i];
        const uCat = categorizeUser(u);
        const others = users.filter((_, idx) => idx !== i);
        const shares = others.some((o) => {
          const oc = categorizeUser(o);
          return oc.gender === uCat.gender && oc.ageBucket === uCat.ageBucket;
        });
        if (shares) satisfied += 1;
      }
      return {
        key: "compatibility" as const,
        label: "Compat",
        requested: 1,
        actual: users.length > 1 ? satisfied / users.length : 1,
      };
    })(),
  ];
}
